// Starting work before it's asked for, so nobody waits for requests one after another:
//  - at boot (main.tsx), the session and the first screen's data and code go out together, instead
//    of the screen's requests waiting for /api/me and then for its code (src/boot.ts has usually
//    sent the reads already, before this code arrived; lib/api.ts adopts those answers);
//  - on intent (a mouse over a link, keyboard focus on one, a tap on the tab bar) and on every
//    navigation, the next screen's code and data start together;
//  - while idle, the signed-in screens' code (one file after another), and the data of the screen
//    people most often open next.
// Data goes through the same query options the screens use (lib/queries.ts), so a screen picks up
// the answer, or the request in flight, instead of asking again. Nothing here posts or changes
// anything: only reads the screen would make anyway.
import type { QueryClient } from '@tanstack/react-query';
import type { createBrowserRouter } from 'react-router';
import { meQuery } from './session';
import { screens } from './screens';
import { whenIdle } from './lazy';
import { initialFaq } from './faq';
import {
  authConfigQuery,
  cashoutConfigQuery,
  catalogFeedQuery,
  catalogQuery,
  dailyQuery,
  faqQuery,
  feedSnapshotQuery,
  leaderboardQuery,
  legacyQuery,
  myOffersQuery,
  offersQuery,
  referralsQuery,
  statsQuery,
  ticketQuery,
  ticketsQuery,
  walletQuery,
  wallsQuery,
  withdrawalsQuery,
} from './queries';
import { preloadAuthForms } from '../components/AuthDialog';

type AppRouter = ReturnType<typeof createBrowserRouter>;
type Options = Parameters<QueryClient['query']>[0];

const noop = () => {};
/** Same request, same cache entry as the screen's useQuery; nothing is sent while it's fresh. */
const get = (qc: QueryClient, options: unknown) => void qc.query(options as Options).catch(noop);
const desktop = () => window.matchMedia('(min-width: 1024px)').matches;
const standalone = () => window.matchMedia('(display-mode: standalone)').matches;

interface Target {
  /** The screen's code. */
  code: () => Promise<unknown>;
  /** The requests its first render makes. */
  data?: (qc: QueryClient, match: RegExpExecArray) => void;
  /** The data needs a session: skipped once we know the visitor is signed out. */
  session?: boolean;
}

const landingData = (qc: QueryClient) => {
  get(qc, statsQuery());
  get(qc, feedSnapshotQuery());
  get(qc, leaderboardQuery('day'));
};

const earn: Target = {
  code: screens.earn,
  session: true,
  data: (qc) => {
    get(qc, wallsQuery());
    get(qc, dailyQuery());
    // Offers straight on Earn: the network takes a second or more, so it starts with the rest.
    get(qc, offersQuery());
    get(qc, myOffersQuery());
    // Earn's desktop side panel; phones show the live ticker (a WebSocket) instead.
    if (desktop()) get(qc, feedSnapshotQuery());
  },
};

/** Paths (no trailing slash) to what their screen needs. */
const ROUTES: [RegExp, Target][] = [
  [/^\/$/, { code: screens.landing, data: landingData }],
  // The installed app's launch URL: signed in, it goes straight on to Earn.
  [/^\/app$/, earn],
  [/^\/earn$/, earn],
  [
    /^\/cashout$/,
    {
      code: screens.cashout,
      session: true,
      data: (qc) => {
        get(qc, cashoutConfigQuery());
        get(qc, withdrawalsQuery());
        // The first page of the catalog, as desktop's pages or the app's "Show more" list asks.
        if (desktop()) get(qc, catalogQuery(null, '', null, 0));
        else
          void qc
            .infiniteQuery(
              catalogFeedQuery<{ pageSize: number; total: number }>(qc, null, '', null),
            )
            .catch(noop);
      },
    },
  ],
  [
    /^\/wallet$/,
    {
      code: screens.wallet,
      session: true,
      data: (qc) => {
        get(qc, walletQuery());
        get(qc, withdrawalsQuery());
        get(qc, legacyQuery());
      },
    },
  ],
  [
    /^\/rewards$/,
    {
      code: screens.rewards,
      session: true,
      data: (qc) => {
        get(qc, dailyQuery());
        get(qc, referralsQuery());
      },
    },
  ],
  [/^\/support$/, { code: screens.support, session: true, data: (qc) => get(qc, ticketsQuery()) }],
  [
    /^\/support\/([^/]+)$/,
    { code: screens.support, session: true, data: (qc, m) => get(qc, ticketQuery(m[1])) },
  ],
  [/^\/account$/, { code: screens.account, session: true }],
  [
    /^\/leaderboard$/,
    { code: screens.leaderboard, data: (qc) => get(qc, leaderboardQuery('day')) },
  ],
  [
    /^\/(signup|login|forgot-password)$/,
    {
      // In a browser: the landing page with the dialog over it. The installed app: its welcome.
      code: () => Promise.all([screens.auth(), preloadAuthForms()]),
      data: (qc) => {
        get(qc, authConfigQuery());
        if (standalone()) get(qc, statsQuery());
        else landingData(qc);
      },
    },
  ],
  [/^\/(reset-password|verify-email|r\/[^/]+)$/, { code: screens.auth }],
  [/^\/(terms|privacy)$/, { code: screens.legal }],
  [/^\/how-it-works$/, { code: screens.howItWorks }],
  [/^\/ways-to-earn$/, { code: screens.waysToEarn }],
  [/^\/gift-cards$/, { code: screens.giftCards }],
  [
    /^\/faq$/,
    {
      code: screens.faq,
      // Not when the Worker served this page with the live FAQ already in it (lib/faq.ts).
      data: (qc) => {
        const first = initialFaq();
        if (!(first?.source === 'served' && first.at)) get(qc, faqQuery());
      },
    },
  ],
];

/** Signed in (true), signed out (false), or not known yet (undefined). */
function session(qc: QueryClient): boolean | undefined {
  const me = qc.getQueryData(meQuery.queryKey);
  return me === undefined ? undefined : me !== null;
}

/** Starts the code, and unless `data` is false the data, of the screen at `pathname`. */
export function prefetchRoute(qc: QueryClient, pathname: string, { data = true } = {}) {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  for (const [re, target] of ROUTES) {
    const m = re.exec(path);
    if (!m) continue;
    target.code().catch(noop);
    if (data && target.data && !(target.session && session(qc) === false)) target.data(qc, m);
    return;
  }
}

/**
 * Before the first render: the session and the first screen's needs, all at once.
 * `prerendered`: #root came with the page's markup, which worker/index.ts only sends to visitors
 * without a session cookie, so the signed-out bits (the sign-up config, member stats) start too.
 * Without it, "/" means a session cookie: Landing will hand over to Earn, so Earn starts as well.
 */
export function bootPrefetch(qc: QueryClient, pathname: string, prerendered: boolean) {
  get(qc, meQuery);
  if (prerendered) {
    get(qc, authConfigQuery());
    get(qc, statsQuery());
  }
  // The app shell on "/" means a session: the member is on their way to Earn (main.tsx Home), so
  // Earn's code and data start now, and the front page's don't.
  if (!prerendered && pathname === '/') return prefetchRoute(qc, '/earn');
  prefetchRoute(qc, pathname);
}

/**
 * Hover (mouse), keyboard focus or a tap on the tab bar or the desktop nav: that screen's code and
 * data. A finger landing elsewhere (often the start of a scroll) only fetches the code; the data
 * starts with the navigation, still together with the code (installNavigationPrefetch).
 */
export function installIntentPrefetch(qc: QueryClient) {
  let last = '';
  const intent = (e: Event, data: boolean) => {
    const a = (e.target as Element | null)?.closest?.('a[href]');
    if (!(a instanceof HTMLAnchorElement)) return;
    if (a.origin !== location.origin || a.target === '_blank' || a.hasAttribute('download')) return;
    if (a.pathname === location.pathname) return;
    const key = `${a.pathname}|${data}`;
    if (key === last) return;
    last = key;
    prefetchRoute(qc, a.pathname, { data });
  };
  document.addEventListener(
    'pointerover',
    (e) => {
      if (e.pointerType === 'mouse') intent(e, true);
    },
    { passive: true },
  );
  document.addEventListener(
    'pointerdown',
    (e) => intent(e, e.pointerType === 'mouse' || !!(e.target as Element).closest?.('nav')),
    { passive: true, capture: true },
  );
  document.addEventListener('focusin', (e) => intent(e, true));
}

/** Every navigation: the router fetches the next screen's code first; its data starts alongside
 *  instead of after it. */
export function installNavigationPrefetch(qc: QueryClient, router: AppRouter) {
  let pending: string | null = null;
  router.subscribe((state) => {
    const to = state.navigation.location;
    if (state.navigation.state === 'idle' || !to) pending = null;
    else if (to.pathname !== pending) {
      pending = to.pathname;
      prefetchRoute(qc, to.pathname);
    }
  });
}

/** Signed-in screens, roughly in the order people open them (the tab bar first); last, the name
 *  form ("Change name" in the account menu). */
const APP_CODE = [
  screens.earn,
  screens.cashout,
  screens.rewards,
  screens.leaderboard,
  screens.support,
  screens.wallet,
  screens.account,
  preloadAuthForms,
];
/** Where people usually go next from each screen: its data is fetched while idle. */
const NEXT: Record<string, string> = {
  '/earn': '/cashout',
  '/wallet': '/cashout',
  '/cashout': '/earn',
  '/rewards': '/earn',
  '/support': '/earn',
  '/leaderboard': '/earn',
  '/account': '/earn',
};

/**
 * While idle (after the screen's own requests have settled): the code of every signed-in screen,
 * one file at a time, so a tab never waits for its code; and the data of the likely next screen,
 * once per session. Signed out: the sign-up dialog's code. Skipped when the visitor asked to save
 * data or the connection is 2G.
 */
export function installIdlePrefetch(qc: QueryClient, router: AppRouter) {
  const conn = (
    navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }
  ).connection;
  if (conn?.saveData || /2g$/.test(conn?.effectiveType ?? '')) return;
  let codeDone = false;
  const fetched = new Set<string>();
  let cancel = noop;

  const run = (path: string) => {
    const signedIn = session(qc);
    if (signedIn === undefined) return;
    if (!codeDone) {
      codeDone = true;
      const queue: (() => Promise<unknown>)[] = signedIn ? APP_CODE : [preloadAuthForms];
      let chain: Promise<unknown> = Promise.resolve();
      for (const load of queue) chain = chain.then(() => load().catch(noop));
    }
    const next = signedIn ? NEXT[path.replace(/(.)\/+$/, '$1')] : undefined;
    if (next && !fetched.has(next)) {
      fetched.add(next);
      prefetchRoute(qc, next);
    }
  };
  /** Waits for the screen's own requests (up to ~10 s), then for an idle moment. */
  const schedule = (path: string) => {
    cancel();
    let tries = 0;
    const attempt = () => {
      if (qc.isFetching() > 0 && tries++ < 20) cancel = whenIdle(attempt, 500);
      else run(path);
    };
    cancel = whenIdle(attempt, 1500);
  };

  qc.query(meQuery)
    .then(() => schedule(router.state.location.pathname))
    .catch(noop);
  let last = router.state.location.pathname;
  router.subscribe((state) => {
    if (state.navigation.state !== 'idle' || state.location.pathname === last) return;
    last = state.location.pathname;
    schedule(last);
  });
}
